import { useCallback, useEffect, useMemo, useState } from "react";
import { FiDownload, FiFilter, FiSearch, FiXCircle } from "react-icons/fi";
import StatusBadge from "components/common/StatusBadge";
import Card from "components/Card";
import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import AppDataTable from "components/common/AppDataTable";
import AppPagination from "components/common/AppPagination";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import FormDatePicker from "components/form/FormDatePicker";
import { formatPortalDateTime, noWrap } from "utils/formatters";
import { apiURl } from "store/actions";
import { buildQueryString } from "utils/helper";
import { api, isSuccessResponse } from "lib/api";
import swtAlert from "lib/sweetAlert";
import { useDebouncedValue } from "hooks/useDebouncedValue";

const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FILTER_INITIAL_VALUES = { page: 1, limit: DEFAULT_LIMIT, search: "", status: "", date: "" };

/** API `query.status` values for `/api/admin/audit-logs`. */
const AUDIT_STATUS_FILTER_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },

];

const AuditStatusCell = (row) => {
  return <StatusBadge status={row.status} />;
};

const mapAuditLogToTableRow = (log) => {
  const first = log.actor?.firstName ?? "";
  const last = log.actor?.lastName ?? "";
  const name = log.actor?.name ?? `${first} ${last}`.trim();
  return {
    id: log.id || log._id,
    user_email: log.actor?.email,
    user_name: name || "—",
    action_type: log.action,
    timestamp: formatPortalDateTime(log.createdAt),
    role: log.actor?.role,
    status: log.status,
  };
};

export default function AuditLogPage() {
  const [data, setData] = useState({});
  const [filters, setFilters] = useState(FILTER_INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  const rows = useMemo(() => data?.data?.map(mapAuditLogToTableRow) ?? [], [data]);
  const totalItems = useMemo(() => data?.total ?? 0, [data]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / (data?.limit || filters.limit))),
    [totalItems, filters.limit, data]
  );

  const columns = useMemo(
    () => [
      { id: "sr_no", header: noWrap("#"), cell: (_row, _index) => _index + 1, sortable: true, width: "80px" },
      { id: "user_email", header: noWrap("User Email"), accessor: "user_email", sortable: true },
      { id: "user_name", header: noWrap("User Name"), accessor: "user_name", sortable: true },
      { id: "action_type", header: noWrap("Action Type"), accessor: "action_type", sortable: true },
      { id: "timestamp", header: noWrap("Timestamp"), accessor: "timestamp", sortable: true },
      { id: "role", header: noWrap("Role"), accessor: "role", sortable: true },
      { id: "status", header: noWrap("Status"), cell: AuditStatusCell, sortable: true },
    ],
    []
  );

  useEffect(() => {
    setListError("");
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endUrl = `${apiURl.auditLogs}`;
      const qs = buildQueryString(filters);
      const path = qs ? `${endUrl}?${qs}` : endUrl;
      const resp = await api.get(path);
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load audit logs.";
        setListError(msg);
        swtAlert.error("Audit Logs", msg);
        return;
      }
      setData(resp);
      setListError("");
    } catch (error) {
      const msg = error?.message || "Could not load audit logs.";
      setListError(msg);
      swtAlert.error("Audit Logs", msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportToCsv = () => {
    const headers = [
      "id",
      "user_email",
      "user_name",
      "action_type",
      "timestamp",
      "role",
      "status",
    ];
    const csvBody = rows.map((row) =>
      headers
        .map((header) => `"${String(row[header] ?? "").replaceAll('"', '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...csvBody].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "audit-logs.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = [filters.status, filters.date].filter(Boolean).length;

  const auditLogCardAction = (
    <div className="inline-flex items-center gap-2">
     
      <button
        type="button"
        onClick={exportToCsv}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-700 transition hover:bg-slate-50"
      >
        <FiDownload className="h-3.5 w-3.5" />
        Export CSV
      </button>
    </div>
  );

  const auditFilterDrawer = filterDrawerOpen ? (
    <div className="mb-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <FormDatePicker
        label="Event date"
        name="audit_list_date"
        value={filters.date}
        onChange={(event) => setFilters({ ...filters, date: event.target.value, page: 1 })}
        placeholder="Filter by date"
      />
      <div>
        <label htmlFor="filter-audit-status" className="mb-1 block text-xs font-semibold text-text-500">
          Status
        </label>
        <select
          id="filter-audit-status"
          value={filters.status === "" ? "all" : filters.status}
          onChange={(event) =>
            setFilters({
              ...filters,
              status: event.target.value === "all" ? "" : event.target.value,
              page: 1,
            })
          }
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none ring-brand-600 focus:ring-2"
        >
          <option value="all">All </option>
          {AUDIT_STATUS_FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  ) : null;

  return (
    <>
      <AppBreadcrumbs items={[{ label: "Audit Log", path: "/audit-log" }]} />
      <Card title="Audit Log" className="mb-4" action={auditLogCardAction} />
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-text-900">Audit Events</h3>
            <p className="text-xs text-text-500">Total logs: {totalItems}</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-500" />
              <input
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    flushSearch();
                    setFilters((prev) => ({
                      ...prev,
                      search: searchDraft.trim(),
                      page: 1,
                    }));
                  }
                }}
                placeholder="Search logs..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none ring-brand-600 focus:ring-2"
              />
            </div>
            <button
              type="button"
              aria-label="Open filters"
              aria-expanded={filterDrawerOpen}
              onClick={() => setFilterDrawerOpen((open) => !open)}
              className="relative rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
            >
              <FiFilter />
              {activeFilterCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-900 px-0.5 text-[10px] font-bold text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            {(searchDraft || filters.status || filters.date) && (
              <button
                type="button"
                aria-label="Clear filters"
                onClick={() => {
                  setSearchDraft("");
                  setFilters(FILTER_INITIAL_VALUES);
                }}
                className="rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
              >
                <FiXCircle />
              </button>
            )}
          </div>
        </div>

        {auditFilterDrawer}

        {listError ? (
          <ListFetchErrorBanner message={listError} onRetry={fetchData} loading={loading} />
        ) : null}

        <AppDataTable columns={columns} data={rows} rowKey="id" loading={loading} />
        <AppPagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={(page) => setFilters({ ...filters, page })}
          totalItems={totalItems}
          pageSize={filters.limit}
          onPageSizeChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </Card>
    </>
  );
}
